// src/components/modules/TAI/TAIAsignacionMasiva.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  message,
  Space,
  Typography,
  Alert,
  Spin,
} from "antd";
import { TeamOutlined, CheckCircleOutlined } from "@ant-design/icons";
import api from "../../../axiosConfig";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const TAIAsignacionMasiva = ({ open, onClose, trasladoIds, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [transportadoras, setTransportadoras] = useState([]);
  const [loadingTransportadoras, setLoadingTransportadoras] = useState(false);

  // Cargar transportadoras activas
  useEffect(() => {
    const fetchTransportadoras = async () => {
      setLoadingTransportadoras(true);
      try {
        const response = await api.get("/tai/transportadoras-activas");
        if (response.data.success) {
          setTransportadoras(response.data.data);
        }
      } catch (error) {
        console.error("Error cargando transportadoras:", error);
        message.error("Error al cargar transportadoras");
      } finally {
        setLoadingTransportadoras(false);
      }
    };

    if (open) {
      fetchTransportadoras();
      form.resetFields();
    }
  }, [open, form]);

  // Manejar envío del formulario
  const handleSubmit = async (values) => {
    if (trasladoIds.length === 0) {
      message.warning("No hay traslados seleccionados");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/tai/asignar-transportadora", {
        traslado_ids: trasladoIds,
        transportadora_id: values.transportadora_id,
        motivo: values.motivo || "Asignación desde módulo TAI",
      });

      if (response.data.success) {
        message.success(response.data.message);
        onSuccess();
      } else {
        message.error(response.data.message || "Error al asignar transportadora");
      }
    } catch (error) {
      console.error("Error en asignación masiva:", error);
      message.error(
        error.response?.data?.message || "Error al realizar la asignación"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined style={{ color: "#722ed1" }} />
          <span>Asignar Transportadora</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={500}
      destroyOnClose
    >
      <Alert
        message={
          <Text>
            Se asignará la transportadora a{" "}
            <Text strong style={{ color: "#722ed1" }}>
              {trasladoIds.length} traslado(s)
            </Text>{" "}
            seleccionados.
          </Text>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="transportadora_id"
          label="Transportadora"
          rules={[
            { required: true, message: "Seleccione una transportadora" },
          ]}
        >
          <Select
            placeholder="Seleccione una transportadora"
            showSearch
            optionFilterProp="children"
            loading={loadingTransportadoras}
            disabled={loadingTransportadoras}
            size="large"
          >
            {transportadoras.map((t) => (
              <Option key={t.id} value={t.id}>
                <Space>
                  <TeamOutlined />
                  {t.nombre}
                  {t.tipo_transporte && (
                    <Text type="secondary">({t.tipo_transporte})</Text>
                  )}
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="motivo"
          label="Motivo de asignación (opcional)"
        >
          <TextArea
            placeholder="Ingrese un motivo o comentario para la asignación..."
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Space>
            <Button onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<CheckCircleOutlined />}
              style={{ backgroundColor: "#722ed1" }}
            >
              Asignar Transportadora
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

TAIAsignacionMasiva.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trasladoIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default TAIAsignacionMasiva;
